import React, { useEffect, useRef, useState } from 'react';
import { 
  TextField, 
  Autocomplete, 
  CircularProgress, 
  Box, 
  Typography,
  Paper,
  styled
} from '@mui/material';
import LocationService from '../services/LocationService';

const SEARCH_DEBOUNCE_MS = 200;
const MIN_QUERY_LENGTH = 2;
const locationService = new LocationService();

// Style the container to ensure full width
const FullWidthBox = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  '& .MuiAutocomplete-root': {
    width: '100%',
     height: '100%'
  },
  '& .MuiInputBase-root': {
    width: '100%',
     height: '100%'
  }
}));

const LocationAutocomplete = ({ onLocationSelect, initialValue }) => {
  const [inputValue, setInputValue] = useState(initialValue || '');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const selectedDescriptionRef = useRef(initialValue || '');

  const clearSelection = () => {
    selectedDescriptionRef.current = '';
    if (onLocationSelect) {
      onLocationSelect(null);
    }
  };

  useEffect(() => {
    setInputValue(initialValue || '');
    selectedDescriptionRef.current = initialValue || '';
  }, [initialValue]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const normalizedQuery = inputValue.trim();
    if (normalizedQuery.length < MIN_QUERY_LENGTH) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      setLoading(true);
      try {
        const results = await locationService.search(normalizedQuery, {
          signal: controller.signal,
          limit: 10,
        });
        setOptions(Array.isArray(results) ? results : []);
      } catch (error) {
        if (error?.name !== 'AbortError') {
          console.error('Local location search failed:', error);
          setOptions([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [inputValue, open]);

  return (
    <FullWidthBox>
      <Autocomplete
        id="location-autocomplete"
        data-testid="location-autocomplete"
        freeSolo
        value={null}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        inputValue={inputValue}
        onInputChange={(event, newInputValue, reason) => {
          setInputValue(newInputValue);

          if (reason === 'clear') {
            clearSelection();
            setOptions([]);
            return;
          }

          if (reason === 'input' && newInputValue !== selectedDescriptionRef.current) {
            clearSelection();
          }
        }}
        onChange={(event, newValue) => {
          if (newValue && typeof newValue === 'object') {
            selectedDescriptionRef.current = newValue.description;
            setInputValue(newValue.description);
            setOptions([]);
            if (onLocationSelect) {
              onLocationSelect(newValue);
            }
          }
        }}
        options={options}
        filterOptions={(availableOptions) => availableOptions}
        getOptionLabel={(option) => {
          return typeof option === 'string' ? option : option.description;
        }}
        isOptionEqualToValue={(option, value) => Boolean(value) && option.searchKey === value.searchKey}
        renderInput={(params) => (
          <TextField
            {...params}
            data-testid="location-autocomplete-input"
            inputRef={inputRef}
            placeholder="Enter a suburb or postcode"
            label="Location/Suburb"
            variant="outlined"
            fullWidth
            sx={{ width: '100%', bgcolor: 'background.input' }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <li 
            {...props} 
            key={`${option.searchKey}-${option.postcode}`}
            data-testid={`location-option-${option.searchKey.replace(/\s+/g, '-')}`}
          >
            {option.description}
          </li>
        )}
        noOptionsText={
          loading ? (
            <Box display="flex" justifyContent="center">
              <CircularProgress size={20} />
            </Box>
          ) : inputValue.trim().length >= MIN_QUERY_LENGTH ? (
            <Typography variant="body2" color="text.secondary">No suggestions found.</Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">Type at least 2 characters</Typography>
          )
        }
        PaperComponent={({ children, ...props }) => (
          <Paper elevation={4} {...props}>
            {children}
          </Paper>
        )}
        sx={{ width: '100%' }}
      />
    </FullWidthBox>
  );
};

export default LocationAutocomplete;
